import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Linkedin, Instagram } from "lucide-react";
import { useLang } from "../i18n";
import { scrollToId } from "../lenis";

const LOGO_WHITE = "https://customer-assets-agu9un31.emergentagent.net/job_smart-locker-hub-5/artifacts/mk8y32ow_ChatGPT%20Image%203%20ago%202026%2C%2016_17_05.png";

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  const goContact = () => {
    if (location.pathname !== "/") navigate("/#contact");
    else scrollToId("contact");
  };

  return (
    <footer className="bg-ink pt-20 text-white" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-12 border-b border-white/10 pb-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="inline-flex rounded-md bg-white px-4 py-2">
              <img src={LOGO_WHITE} alt="Boxly" className="h-6 w-auto" />
            </div>
            <p className="mt-6 max-w-sm font-display text-2xl font-bold leading-tight text-white/90">
              {t.footer.tagline}
            </p>
            <p className="mt-4 font-body text-sm text-white/60">
              {t.footer.brandOfIt}
            </p>
          </div>

          <div className="lg:col-span-4">
            <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-lime-accent">{t.footer.contact}</h4>
            <ul className="mt-5 space-y-4 font-body text-white/70">
              <li>
                <span className="block font-mono text-[10px] uppercase tracking-widest text-white/40">{t.footer.generalEmail}</span>
                <a href="mailto:info@boxlyitalia.it" className="transition-colors hover:text-lime-accent" data-testid="footer-general-email">info@boxlyitalia.it</a>
              </li>
              <li>
                <span className="block font-mono text-[10px] uppercase tracking-widest text-white/40">{t.footer.commercialEmail}</span>
                <a href="mailto:emanuele@boxlyitalia.it" className="transition-colors hover:text-lime-accent" data-testid="footer-commercial-email">emanuele@boxlyitalia.it</a>
              </li>
              <li>
                <span className="block font-mono text-[10px] uppercase tracking-widest text-white/40">PEC</span>
                <a href="mailto:newnetworksrls@pec.it" className="transition-colors hover:text-lime-accent" data-testid="footer-pec-email">newnetworksrls@pec.it</a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-lime-accent">{t.footer.legal}</h4>
            <ul className="mt-5 space-y-3 font-body text-white/70">
              <li>
                <button onClick={goContact} className="transition-colors hover:text-lime-accent" data-testid="footer-contact-link">
                  {t.footer.contactLink}
                </button>
              </li>
              <li>
                <Link to="/privacy-policy" className="transition-colors hover:text-lime-accent" data-testid="footer-privacy">{t.footer.privacy}</Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="transition-colors hover:text-lime-accent" data-testid="footer-cookie">{t.footer.cookie}</Link>
              </li>
              <li>
                <Link to="/informazioni-societarie" className="transition-colors hover:text-lime-accent" data-testid="footer-legal-info">{t.footer.legalInfo}</Link>
              </li>
            </ul>

            <div className="mt-8">
              <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-lime-accent">{t.footer.social}</h4>
              <div className="mt-4 flex gap-3">
                {[Linkedin, Instagram].map((Icon, i) => (
                  <a key={i} href="#" onClick={(e) => e.preventDefault()} className="flex h-10 w-10 items-center justify-center rounded-md border border-white/15 transition-colors hover:border-lime-accent hover:bg-lime-accent hover:text-ink" data-testid={`social-${i}`} aria-label="social">
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Institutional legal block (discreet) */}
        <div className="border-b border-white/10 py-10" data-testid="legal-block">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{t.footer.legalHolder}</p>
          <div className="mt-3 grid grid-cols-1 gap-x-8 gap-y-2 font-body text-xs leading-relaxed text-white/55 sm:grid-cols-2 lg:grid-cols-3">
            <p><span className="text-white/40">Ragione sociale:</span> NEWNETWORK S.R.L.S.</p>
            <p><span className="text-white/40">Forma giuridica:</span> S.r.l.s. — socio unico</p>
            <p><span className="text-white/40">Sede legale:</span> Corso San Giovanni a Teduccio 440, 80146 Napoli (NA), Italia</p>
            <p><span className="text-white/40">P.IVA / C.F.:</span> 10290721215</p>
            <p><span className="text-white/40">Registro Imprese:</span> Napoli</p>
            <p><span className="text-white/40">REA:</span> NA-1094905</p>
            <p><span className="text-white/40">Capitale sociale:</span> € 2.500,00 i.v.</p>
            <p><span className="text-white/40">PEC:</span> newnetworksrls@pec.it</p>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 py-8 sm:flex-row">
          <p className="font-body text-sm text-white/50">© {year} BOXLY — NEWNETWORK S.R.L.S. {t.footer.rights}</p>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">Smart Parcel Lockers</p>
        </div>
      </div>
    </footer>
  );
}
